import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Paper } from '@mui/material';
import AUHeader from '../components/AUHeader';
import VideoAccessForm from '../components/VideoAccessForm';
import { hasValidVideoAccess, setStoredVideoAccess } from '../utils/videoAccessStorage';
import { submitLead } from '../utils/submitLead';

const BLUE = '#1A7FFF';
const BLUE_HOVER = '#1568D9';
const BLUE_SOFT = '#EBF3FF';
const DARK = '#0F1117';
const GRAY = '#64748B';
const BORDER = '#E2E8F0';
const GRAY_LIGHT = '#F8FAFC';

// Los tres protocolos que existen hoy. Cada uno es un PDF distinto en /recursos.
const PROFILES = [
  {
    id: 'hombre-natural',
    label: 'Hombre natural',
    sub: 'Sin uso de anabólicos',
    pdf: '/recursos/examenes-naturales-au-coaching.pdf',
    note: 'El control base para un atleta natural: función renal, hepática, perfil lipídico, hormonal y tiroides.',
  },
  {
    id: 'hombre-anabolicos',
    label: 'Hombre usuario de anabólicos',
    sub: 'Con uso de anabólicos',
    pdf: '/recursos/examenes-usuario-anabolicos-au-coaching.pdf',
    note: 'Mismo control base, pero con los marcadores que dejan de ser opcionales cuando usas anabólicos.',
  },
  {
    id: 'mujer-natural',
    label: 'Mujer natural',
    sub: 'Sin uso de anabólicos',
    pdf: '/recursos/examenes-naturales-mujer-au-coaching.pdf',
    note: 'Incluye el perfil hormonal femenino y en qué día del ciclo se toma cada hormona.',
  },
];

// Categorías que cubre el protocolo. Son las mismas en los tres PDFs;
// lo que cambia entre perfiles es el detalle de cada una.
const CATEGORIES = [
  { name: 'Función renal', detail: 'Creatinina, cistatina C' },
  { name: 'Inflamación', detail: 'Proteína C reactiva (PCR)' },
  { name: 'Perfil lipídico', detail: 'Colesterol total, LDL, HDL, triglicéridos, ApoB' },
  { name: 'Función hepática', detail: 'Perfil hepático completo' },
  { name: 'Perfil bioquímico', detail: 'Perfil bioquímico completo' },
  { name: 'Hematología', detail: 'Hemograma, ferritina' },
  { name: 'Metabolismo de glucosa', detail: 'Hemoglobina glicosilada A1c, insulina basal' },
  { name: 'Perfil hormonal', detail: 'Testosterona, estradiol, SHBG, LH, FSH, prolactina, IGF-1' },
  { name: 'Tiroides', detail: 'TSH, T4 libre, T3 libre' },
  { name: 'Micronutrientes y electrolitos', detail: 'Vitamina D, B12, sodio, potasio, cloro' },
  { name: 'Orina', detail: 'Orina completa' },
];

function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontFamily: 'Kanit, sans-serif', fontWeight: 800, color: DARK,
      fontSize: { xs: '1.3rem', md: '1.6rem' }, mb: 2.5, mt: 1,
    }}>
      {children}
    </Typography>
  );
}

function ProfileCard({ profile, selected, onSelect }) {
  return (
    <Paper
      elevation={0}
      onClick={() => onSelect(profile.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(profile.id); } }}
      sx={{
        flex: 1, p: 2.25, borderRadius: 3, cursor: 'pointer',
        bgcolor: selected ? BLUE_SOFT : '#fff',
        border: `1px solid ${selected ? BLUE : BORDER}`,
        borderLeft: `4px solid ${selected ? BLUE : BORDER}`,
        transition: 'background-color .2s ease, border-color .2s ease',
        '&:hover': { borderColor: BLUE, bgcolor: selected ? BLUE_SOFT : GRAY_LIGHT },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
        <Box sx={{
          width: 18, height: 18, borderRadius: '50%', flexShrink: 0, mt: 0.35,
          border: `2px solid ${selected ? BLUE : BORDER}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {selected && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: BLUE }} />}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, color: selected ? BLUE_HOVER : DARK, fontFamily: 'Kanit, sans-serif', fontSize: 15.5, mb: 0.25 }}>
            {profile.label}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: GRAY, fontFamily: 'Kanit, sans-serif' }}>
            {profile.sub}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

const BloodWorkGuidePage = () => {
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [profileId, setProfileId] = useState(PROFILES[0].id);

  const profile = PROFILES.find((p) => p.id === profileId);

  const handleUnlock = () => {
    if (hasValidVideoAccess()) {
      setUnlocked(true);
    } else {
      setShowLeadForm(true);
    }
  };

  const handleLeadSubmit = async (data) => {
    setShowLeadForm(false);
    setStoredVideoAccess(data);
    await submitLead({ data, source: 'blood-work' });
    setUnlocked(true);
  };

  return (
    <Box sx={{ bgcolor: GRAY_LIGHT, fontFamily: 'Kanit, sans-serif', minHeight: '100vh' }}>
      <AUHeader />

      <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 6, md: 10 } }}>
        {/* Hero */}
        <Typography sx={{
          fontSize: 15, fontWeight: 700, letterSpacing: 2.5, color: BLUE,
          textTransform: 'uppercase', mb: 0.75, fontFamily: 'Kanit, sans-serif',
        }}>
          Material gratis
        </Typography>
        <Typography component="h1" sx={{
          fontFamily: 'Kanit, sans-serif', fontWeight: 800,
          fontSize: { xs: '2rem', md: '2.8rem' }, color: DARK, mb: 1,
        }}>
          Exámenes de sangre y control médico
        </Typography>
        <Typography sx={{ color: GRAY, mb: 4, maxWidth: 680, fontFamily: 'Kanit, sans-serif' }}>
          La lista exacta de exámenes que le pido a mis alumnos antes de empezar, para llevársela al médico
          o al laboratorio tal cual. Hay una versión por perfil: hombre natural, hombre usuario de anabólicos
          y mujer natural.
        </Typography>

        {!unlocked ? (
          /* Gate — el protocolo completo vive detrás del formulario */
          <Paper elevation={0} sx={{
            borderRadius: 3, border: `1px solid ${BLUE}`, bgcolor: '#fff',
            p: { xs: 3, md: 5 }, textAlign: 'center', mt: 1,
          }}>
            <Typography sx={{ fontWeight: 800, color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: { xs: '1.4rem', md: '1.9rem' }, mb: 1 }}>
              Desbloquea el protocolo completo
            </Typography>
            <Typography sx={{ color: GRAY, fontFamily: 'Kanit, sans-serif', mb: 3, maxWidth: 560, mx: 'auto' }}>
              Déjanos tus datos y accede al instante a las tres versiones:
            </Typography>
            <Box sx={{ maxWidth: 520, mx: 'auto', textAlign: 'left', mb: 3.5 }}>
              {[
                'Protocolo para hombre natural',
                'Protocolo para hombre usuario de anabólicos',
                'Protocolo para mujer natural, con el día del ciclo para cada hormona',
                'Cómo prepararte antes de la toma + el PDF descargable de cada uno',
              ].map((item) => (
                <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.25 }}>
                  <Box sx={{ color: BLUE, fontWeight: 800, fontFamily: 'Kanit, sans-serif', fontSize: 18, lineHeight: 1.35 }}>✓</Box>
                  <Typography sx={{ color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 15 }}>{item}</Typography>
                </Box>
              ))}
            </Box>
            <Button
              variant="contained" disableElevation onClick={handleUnlock}
              sx={{
                py: 1.5, px: 5, fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: 16,
                textTransform: 'none', bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER },
              }}
            >
              Ver el protocolo completo
            </Button>
            <Typography sx={{ color: GRAY, fontFamily: 'Kanit, sans-serif', fontSize: 12.5, mt: 2 }}>
              Gratis. Solo te pedimos tus datos para enviártelo y acompañarte en el proceso.
            </Typography>
          </Paper>
        ) : (
        <>
          {/* Selector de perfil */}
          <SectionTitle>Elige tu perfil</SectionTitle>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 3 }}>
            {PROFILES.map((p) => (
              <ProfileCard key={p.id} profile={p} selected={p.id === profileId} onSelect={setProfileId} />
            ))}
          </Box>

          {/* Panel del perfil elegido + descarga */}
          <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: `1px solid ${BLUE}`, bgcolor: '#fff', mb: 5 }}>
            <Box sx={{ bgcolor: BLUE, px: { xs: 2, md: 3 }, py: 1.25 }}>
              <Typography sx={{ fontWeight: 800, color: '#fff', fontFamily: 'Kanit, sans-serif', fontSize: { xs: 15, md: 17 } }}>
                {profile.label}
              </Typography>
            </Box>
            <Box sx={{ px: { xs: 2, md: 3 }, py: 2.5 }}>
              <Typography sx={{ color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 14.5, mb: 2 }}>
                {profile.note}
              </Typography>
              <Button
                variant="contained" disableElevation component="a" href={profile.pdf} download
                sx={{
                  py: 1.3, px: 3.5, fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: 15,
                  textTransform: 'none', bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER },
                }}
              >
                Descargar PDF
              </Button>
            </Box>
          </Paper>

          {/* Cómo prepararte */}
          <Paper elevation={0} sx={{
            p: { xs: 2.5, md: 3 }, mb: 5, borderRadius: 3,
            bgcolor: BLUE_SOFT, border: `1px solid ${BLUE}`, borderLeft: `4px solid ${BLUE}`,
          }}>
            <Typography sx={{ fontWeight: 800, color: BLUE_HOVER, fontFamily: 'Kanit, sans-serif', fontSize: 15, mb: 1, textTransform: 'uppercase', letterSpacing: 0.3 }}>
              Antes de ir al laboratorio
            </Typography>
            <Typography sx={{ color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 14.5 }}>
              Toma entre 500 ml y 1 litro de agua entre 1 hora y 30 minutos antes del examen, y asiste en ayunas
              según la indicación del laboratorio. Si eres mujer, las hormonas sexuales (estradiol, progesterona,
              FSH y LH) se toman entre el día 2 y 5 del ciclo menstrual.
            </Typography>
          </Paper>

          {/* Qué cubre el protocolo */}
          <SectionTitle>Qué cubre el protocolo</SectionTitle>
          <Typography sx={{ color: GRAY, mb: 2.5, maxWidth: 760, fontFamily: 'Kanit, sans-serif', fontSize: 14.5 }}>
            Las áreas son las mismas en los tres perfiles. Lo que cambia es el detalle de cada una: qué marcador
            es opcional, cuál deja de serlo y cuándo se toma. Eso está en el PDF de tu perfil.
          </Typography>
          <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, overflow: 'hidden', mb: 5 }}>
            {CATEGORIES.map((c, i) => (
              <Box key={c.name} sx={{
                display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'baseline' }, gap: { xs: 0.25, sm: 1.5 },
                px: 2.5, py: 1.5, bgcolor: i % 2 === 0 ? BLUE_SOFT : '#fff',
              }}>
                <Typography sx={{ color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 14.5, fontWeight: 700, minWidth: { sm: 250 } }}>
                  {c.name}
                </Typography>
                <Typography sx={{ color: GRAY, fontFamily: 'Kanit, sans-serif', fontSize: 14 }}>
                  {c.detail}
                </Typography>
              </Box>
            ))}
          </Paper>

          {/* Disclaimer médico */}
          <Typography sx={{ color: GRAY, fontFamily: 'Kanit, sans-serif', fontSize: 13, mb: 1, maxWidth: 760 }}>
            Este protocolo es una referencia orientativa. La interpretación de los resultados debe hacerla un médico
            de confianza, idealmente con contexto deportivo o en medicina del deporte.
          </Typography>

          {/* CTA a coaching */}
          <Paper elevation={0} sx={{ mt: 5, p: { xs: 3, md: 4 }, borderRadius: 3, bgcolor: DARK }}>
            <Typography sx={{ color: '#fff', fontWeight: 800, fontFamily: 'Kanit, sans-serif', fontSize: { xs: 18, md: 22 }, mb: 0.75 }}>
              ¿Y si alguien leyera esos números contigo?
            </Typography>
            <Typography sx={{ color: '#CBD5E1', fontFamily: 'Kanit, sans-serif', fontSize: 14.5, mb: 2.5, maxWidth: 640 }}>
              Tener los exámenes es el primer paso. Ajustar entrenamiento y nutrición según lo que muestran, con
              seguimiento real semana a semana, es lo que hacemos en AU Coaching.
            </Typography>
            <Button
              variant="contained" disableElevation onClick={() => navigate('/form')}
              sx={{
                py: 1.3, px: 3.5, fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: 15,
                textTransform: 'none', bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER },
              }}
            >
              Postula a AU Coaching
            </Button>
          </Paper>
        </>
        )}
      </Container>

      <VideoAccessForm
        open={showLeadForm}
        onClose={() => setShowLeadForm(false)}
        onSubmit={handleLeadSubmit}
        title="Desbloquea el protocolo de exámenes"
        subtitle="Déjanos tus datos y accede al instante a los tres protocolos, con el PDF para descargar."
        submitLabel="Ver el protocolo completo"
      />
    </Box>
  );
};

export default BloodWorkGuidePage;
